import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { faMoneyBillTransfer } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ApplyForIbanModal from "../../Components/Common/ApplyForIbanModal";
import AppButton from "../../Components/Common/Button";
import SurfaceCard from "../../Components/Common/SurfaceCard";
import useNewBslAccountAPI from "../../api/bslAccountAPI";
import useNewS2SAPI from "../../api/s2sAPI";
import { CURRENCY_IDS } from "../../constants/currency";
import { setFeatureData } from "../../store/slices/appSlice";
import { BSL_STATUS } from "../../Components/Sidebar/utils/sidebarStorage";
import { createDefaultDateRange } from "../../utils/dateRange";
import { roleAuth } from "../../../utils/functionAuthorization";
import BulkPaymentForm from "./components/BulkPaymentForm";
import MakePaymentForm from "./components/MakePaymentForm";
import PaymentMethodSelector from "./components/PaymentMethodSelector";
import QuickAccessCards from "./components/QuickAccessCards";
import RecentPaymentsCard from "./components/RecentPaymentsCard";
import "../../Styles/pages/make-payment.scss";

const INITIAL_FORM = {
  amount: "",
  currencyId: CURRENCY_IDS.USD,
  phoneNumber: "",
  recipientType: "individual",
  sendMode: "one",
};

const ACCESS_POINTS = {
  bankTransfer: "INITIATE_BANK_TRANSFERS",
  bulkTransfer: "INITIATE_A_NEW_BULK_SUYOOL_TO_SUYOOL_TRANSFER",
  suyoolTransfer: "INITIATE_A_NEW_SINGLE_SUYOOL_TO_SUYOOL_TRANSFER",
};

function normalizeBslStatus(value) {
  const status = Number(value);
  return Object.values(BSL_STATUS).includes(status) ? status : null;
}

export default function MakePaymentPage({ onOpenQrAfterGenerate }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { GetBankAccountStatus } = useNewBslAccountAPI();
  const { GetS2STransfersList } = useNewS2SAPI();
  const getBankAccountStatusRef = useRef(GetBankAccountStatus);
  const getS2STransfersListRef = useRef(GetS2STransfersList);
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState("form");
  const [applyIbanOpen, setApplyIbanOpen] = useState(false);
  const canCreateBslTransfer = roleAuth(
    "bankaccounttransfers",
    ACCESS_POINTS.bankTransfer,
  );
  const canCreateSuyoolTransfer = roleAuth(
    "suyooltosuyool",
    ACCESS_POINTS.suyoolTransfer,
  );
  const canCreateBulkTransfer = roleAuth(
    "suyooltosuyool",
    ACCESS_POINTS.bulkTransfer,
  );
  getBankAccountStatusRef.current = GetBankAccountStatus;
  getS2STransfersListRef.current = GetS2STransfersList;

  const storeBslStatus = useSelector(
    (state) => state?.app?.summary?.bankAccountStatus,
  );
  const recentSuyoolPayments = useSelector(
    (state) => state?.app?.tables?.suyoolToSuyool?.data || [],
  );

  const bslStatus = useMemo(() => {
    const fromStore = normalizeBslStatus(storeBslStatus);
    if (fromStore !== null) return fromStore;
    return normalizeBslStatus(sessionStorage.getItem("BslStatus"));
  }, [storeBslStatus]);

  useEffect(() => {
    dispatch(
      setFeatureData({
        section: "forms",
        key: "makePayment",
        value: form,
      }),
    );
  }, [dispatch, form]);

  useEffect(() => {
    let isMounted = true;

    const refreshBslStatus = async () => {
      try {
        await getBankAccountStatusRef.current();
      } catch {
        if (isMounted) {
          dispatch(
            setFeatureData({
              section: "summary",
              key: "bankAccountStatusError",
              value: "Unable to check BSL account status.",
            }),
          );
        }
      }
    };

    void refreshBslStatus();

    const handleIbanStatusUpdated = () => {
      void refreshBslStatus();
    };

    window.addEventListener("new:iban-status-updated", handleIbanStatusUpdated);
    return () => {
      isMounted = false;
      window.removeEventListener(
        "new:iban-status-updated",
        handleIbanStatusUpdated,
      );
    };
  }, [dispatch]);

  useEffect(() => {
    const { from, to } = createDefaultDateRange();
    getS2STransfersListRef.current(0, from, to).catch(() => {});
  }, []);

  const updateForm = useCallback((field, value) => {
    setForm((current) => {
      const next = { ...current, [field]: value };
      if (field === "recipientType" && value === "business") {
        next.phoneNumber = "";
      }
      return next;
    });
    setErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
    if (field === "sendMode") {
      setStep("form");
    }
  }, []);

  const validateSinglePayment = useCallback(() => {
    const nextErrors = {};
    const isIndividual = form.recipientType !== "business";

    if (isIndividual && !form.phoneNumber) {
      nextErrors.phoneNumber = "Recipient phone number is required.";
    }

    if (!form.currencyId) {
      nextErrors.currencyId = "Account is required.";
    }

    if (!form.amount) {
      nextErrors.amount = "Amount is required.";
    } else if (Number.isNaN(Number(form.amount)) || Number(form.amount) <= 0) {
      nextErrors.amount = "Amount must be a positive number.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [form.amount, form.currencyId, form.phoneNumber, form.recipientType]);

  const prefill = useMemo(
    () => ({
      amount: form.amount,
      currencyId: form.currencyId,
      phoneNumber: form.phoneNumber,
      recipientType: form.recipientType,
    }),
    [form.amount, form.currencyId, form.phoneNumber, form.recipientType],
  );

  const goToSuyoolTransfer = useCallback(
    (withPrefill = false) => {
      navigate("/suyooltosuyool?tab=new-transfer", {
        state: {
          name: "+ New Transfer",
          prefill: withPrefill ? prefill : undefined,
        },
      });
    },
    [navigate, prefill],
  );

  const goToBulkTransfer = useCallback(() => {
    navigate("/suyooltosuyool?tab=bulk-transfer", {
      state: { name: "+ Bulk Transfer" },
    });
  }, [navigate]);

  const goToBslTransfer = useCallback(
    (withPrefill = false) => {
      navigate("/bankaccounttransfers?tab=new-transfer", {
        state: {
          name: "New Transfer",
          prefill: withPrefill ? prefill : undefined,
        },
      });
    },
    [navigate, prefill],
  );

  const goToInternationalTransfer = useCallback(() => {
    navigate("/transferabroad?tab=new-transfer", {
      state: { name: "+ New Transfer" },
    });
  }, [navigate]);

  const handleQuickAccess = useCallback(
    (key) => {
      if (key === "bank-transfer") {
        goToBslTransfer(false);
        return;
      }

      if (key === "suyool-transfer") {
        goToSuyoolTransfer(false);
        return;
      }

      if (key === "bulk-transfer") {
        goToBulkTransfer();
        return;
      }

      if (key === "international-transfer") {
        goToInternationalTransfer();
      }
    },
    [
      goToBslTransfer,
      goToBulkTransfer,
      goToInternationalTransfer,
      goToSuyoolTransfer,
    ],
  );

  const handleContinue = useCallback(() => {
    if (!validateSinglePayment()) return;
    setStep("method");
  }, [validateSinglePayment]);

  const handleSelectBsl = useCallback(() => {
    goToBslTransfer(true);
  }, [goToBslTransfer]);

  const handleSelectSuyool = useCallback(() => {
    goToSuyoolTransfer(true);
  }, [goToSuyoolTransfer]);

  return (
    <section className="new-page-shell make-payment-page">
      <div className="make-payment-page__heading">
        <span className="make-payment-page__heading-icon" aria-hidden="true">
          <FontAwesomeIcon icon={faMoneyBillTransfer} />
        </span>
        <div>
          <h1>Make Payment</h1>
          <p>Start a transfer quickly, then continue in the matching payment flow.</p>
        </div>
      </div>

      <QuickAccessCards onSelect={handleQuickAccess} />

      <section className="make-payment-page__payment" aria-labelledby="payment-title">
        <h2 id="payment-title">Payment</h2>

        <div className="make-payment-page__payment-grid">
          <SurfaceCard className="make-payment-page__form-card">
            {form.sendMode === "one" && step === "method" ? (
              <PaymentMethodSelector
                bslStatus={bslStatus}
                canUseBsl={canCreateBslTransfer}
                canUseSuyool={canCreateSuyoolTransfer}
                onApplyIban={() => setApplyIbanOpen(true)}
                onBack={() => setStep("form")}
                onSelectBsl={handleSelectBsl}
                onSelectSuyool={handleSelectSuyool}
              />
            ) : (
              <>
                <MakePaymentForm
                  amount={form.amount}
                  currencyId={form.currencyId}
                  errors={errors}
                  onChange={updateForm}
                  phoneNumber={form.phoneNumber}
                  recipientType={form.recipientType}
                  sendMode={form.sendMode}
                />

                {form.sendMode === "one" ? (
                  <AppButton
                    type="button"
                    className="make-payment-page__continue"
                    onClick={handleContinue}
                  >
                    Continue
                  </AppButton>
                ) : canCreateBulkTransfer ? (
                  <BulkPaymentForm onOpenQrAfterGenerate={onOpenQrAfterGenerate} />
                ) : (
                  <div className="make-payment-methods__notice">
                    Bulk transfer is not available for this account right now.
                  </div>
                )}
              </>
            )}
          </SurfaceCard>

          <RecentPaymentsCard rows={recentSuyoolPayments} />
        </div>
      </section>

      <ApplyForIbanModal
        open={applyIbanOpen}
        onClose={() => setApplyIbanOpen(false)}
      />
    </section>
  );
}
